/**
 * Paired English / Hinglish CONTENT dictionary.
 *
 * The UI dictionary (`dictionary.ts`) holds chrome and short labels. This file
 * holds long-form, per-entity sentences: every product, every collection page
 * and every routine (bundle / goal / timeline).
 *
 * Rules (same as dictionary.ts, repeated because this file is edited by copy
 * writers rather than engineers):
 *  - The English source of truth stays in its own module (`collections.ts`,
 *    `quizRouter.ts`, `fallbackProducts.ts`, `bundles.ts`). This file only adds
 *    the Hinglish half, keyed by the same stable id/handle/slug.
 *  - A missing Hinglish sentence falls back to the COMPLETE English sentence —
 *    never a partial mix inside one paragraph.
 *  - NEVER translate: product names, trademarks, ingredient names and delivery
 *    forms, doses, units, ratios, prices, pack sizes, certification or licence
 *    names, study titles, journals and citations.
 *  - NEVER strengthen a claim: "supports" → "support karta hai", never
 *    "theek kar deta hai" / "cure".
 */

import { useCallback } from "react";
import { useLanguage, pickLocalized } from "./provider";

export type ProductHandle = "mag-absorb-pro" | "liver-biome-plus" | "ova-balance";

export type ProductCopy = {
  tagline: string;
  description: string;
  bullets: string[];
  why: string;
  timeline: { day7: string; day15: string; day30: string };
  routine: string;
};

/** Hinglish half for each SKU. Names, forms, doses and ratios stay as-is. */
export const PRODUCT_HI: Record<ProductHandle, ProductCopy> = {
  "mag-absorb-pro": {
    tagline: "5-form magnesium — neend, calm aur recovery ke liye",
    description:
      "5-form magnesium matrix, liposomal bisglycinate + Aquamin® ke saath — gehri neend, shaant nerves aur behtar recovery ko support karne ke liye. Research-backed doses, vegan, third-party lab tested.",
    bullets: [
      "Liposomal Magnesium Bisglycinate + Aquamin® marine magnesium",
      "Chloride, Aspartate aur Gluconate — full-spectrum coverage ke liye",
      "Zinc, Copper, P-5-P aur Vitamin D3 cofactors, ek hi tablet mein",
    ],
    why: "Magnesium wo cofactor hai jis par aapka nervous system, muscles aur sleep architecture chalte hain. Yahan ka 5-form matrix — liposomal bisglycinate aur Aquamin® marine magnesium par based, saath mein chloride, aspartate aur gluconate staged uptake ke liye — zinc, copper, P-5-P aur D3 cofactors ke saath aata hai, taaki body ise sach mein use kar sake.",
    timeline: {
      day7: "Shaam zyada shaant, raat mein baar-baar neend tootna kam.",
      day15: "Neend gehri, cramps kam, subah energy zyada steady.",
      day30: "Recovery mein saaf farq aur roz ka stress response zyada shaant.",
    },
    routine: "Roz raat ko sone se 30-60 minute pehle 1 tablet, paani ke saath.",
  },
  "liver-biome-plus": {
    tagline: "Liposomal liver support + multi-strain probiotic",
    description:
      "Liposomal liver aur gut support — curcumin, silymarin, ALA aur NAC ke saath, plus 10B CFU multi-strain probiotic. Bloating, thakaan aur roz ke detox ko jad se support karta hai.",
    bullets: [
      "NAC 600 mg, liposomal Silymarin (350 mg eq) ke saath",
      "Liposomal Curcumin aur Alpha-Lipoic Acid",
      "10B CFU multi-strain probiotic — gut-liver axis ke liye",
    ],
    why: "Liver Biome Plus wahi cofactors deta hai jin par liver ke Phase I aur Phase II pathways chalte hain. Liposomal carriers aur Bio-Absorb Matrix ke saath banaya gaya hai, taaki silymarin, curcumin aur ALA ki delivery ko support kiya ja sake.",
    timeline: {
      day7: "Subah halki lagti hai, khane ke baad bloating kam.",
      day15: "Digestion smooth, din bhar energy zyada steady.",
      day30: "Skin saaf, bowels regular, aur roz ki thakaan mein saaf kami.",
    },
    routine: "Roz subah khane ke saath 1 tablet, paani ke saath.",
  },
  "ova-balance": {
    tagline: "Myo + D-Chiro 40:1 — cycle, mood aur metabolic balance ke liye",
    description:
      "Roz ka PCOS support, ek consistent routine ke liye. Ova Balance mein Myo- aur D-Chiro Inositol 40:1 ratio mein, Quatrefolic® L-5-MTHF aur Berberine HCl ke saath — cycle, skin aur metabolic wellness ko support karne ke liye, aapke bade PCOS-care plan ka hissa banke.",
    bullets: [
      "Gold-standard 40:1 Myo + D-Chiro Inositol (2050 mg)",
      "Berberine HCl 300 mg, Quatrefolic® L-5-MTHF ke saath",
      "Magnesium, Zinc, Chromium aur Vitamin D3 cofactors",
    ],
    why: "Ova Balance wahi 40:1 myo-to-D-chiro inositol ratio use karta hai jo published PCOS trials mein validate hua hai, plus Berberine HCl Bio-Absorb Matrix ke saath — insulin sensitivity ko support karne ke liye, jo cycle, skin aur weight ka upstream lever hai.",
    timeline: {
      day7: "Sugar cravings kam, din bhar mood zyada steady.",
      day15: "Skin saaf, PMS wali irritability kam.",
      day30: "Cycles zyada regular aur metabolic baseline aasan — zyadatar log 3 cycles mein saaf farq dekhte hain.",
    },
    routine: "Roz ek sachet, khane ke baad, 200 ml paani mein mila kar.",
  },
};

/** English fallbacks for the two fields that have no single English home. */
export const PRODUCT_ROUTINE_EN: Record<ProductHandle, string> = {
  "mag-absorb-pro": "1 tablet daily, 30-60 minutes before bed, with water.",
  "liver-biome-plus": "1 tablet daily in the morning, with food and water.",
  "ova-balance": "One sachet daily after a meal, mixed into 200 ml water.",
};

export type CollectionSlug =
  | "magnesium-supplements"
  | "sleep-recovery"
  | "inositol-supplements"
  | "pcos-nutrition"
  | "liver-gut-support"
  | "kids-nutrition";

export type CollectionCopy = {
  h1: string;
  eyebrow: string;
  intro: string;
  overview: string;
  guideHeading: string;
  guideIntro: string;
  guideItems: { title: string; body: string }[];
  safety: string[];
  faqs: { q: string; a: string }[];
  productTaglines?: Partial<Record<ProductHandle, string>>;
};

export const COLLECTION_HI: Record<CollectionSlug, CollectionCopy> = {
  "magnesium-supplements": {
    eyebrow: "Magnesium collection",
    h1: "Magnesium supplements — us form ke hisaab se chune, jo body sach mein absorb karti hai",
    intro:
      "Sabhi magnesium ek jaise nahi hote. Label par likhi form hi decide karti hai ki body kitna absorb karegi aur wo kahan kaam karega — neend, muscle, mood ya digestion.",
    overview:
      "India ki zyadatar magnesium tablets magnesium oxide use karti hain — sasta form, lekin oral absorption limited. TruWe ek hi tablet mein paanch better-studied forms use karta hai — liposomal magnesium bisglycinate, Aquamin® marine magnesium, magnesium chloride, aspartate aur gluconate — taaki body mein magnesium ki poori range cover ho aur kisi ek salt ke trade-offs kam hon.",
    guideHeading: "Apna magnesium kaise chunein",
    guideIntro:
      "Us outcome ke hisaab se chunein jo aapke liye maayne rakhta hai — tin par likhe milligrams se zyada important underlying form hai.",
    guideItems: [
      {
        title: "Neend, stress aur calm ke liye",
        body: "Magnesium bisglycinate dekhein. Glycine ek calming amino acid hai, aur ye chelated form khaali pet par bhi aaram se seh li jaati hai.",
      },
      {
        title: "Muscle cramps aur recovery ke liye",
        body: "Aise blends kaam ke hain jinmein Aquamin® marine magnesium aur magnesium chloride ho — chloride aasaani se ionise hota hai aur marine magnesium naturally aane wale trace minerals deta hai.",
      },
      {
        title: "Roz ke general support ke liye",
        body: "Multi-form blend wo gaps cover karta hai jo ek single salt nahi kar sakta. Bisglycinate ke saath aspartate aur gluconate nervous-system aur cellular-energy dono pathways ko support karte hain.",
      },
      {
        title: "Jin forms se hum jaan-boojh kar bachte hain",
        body: "Magnesium oxide (low bioavailability) aur akela high-dose magnesium citrate (active doses par loose stools). Label zaroor padhein.",
      },
    ],
    safety: [
      "Agar aap blood-pressure ki dawa, diuretics ya bisphosphonates lete hain, to magnesium supplement shuru karne se pehle apne doctor se baat karein.",
      "Pregnant ya nursing hain? Apne clinician se poochein — roz ke magnesium targets alag hote hain.",
      "Ye ek food supplement hai, balanced diet ya medical care ka replacement nahi.",
    ],
    faqs: [
      {
        q: "Kaunsi magnesium form sabse zyada bioavailable hai?",
        a: "Magnesium bisglycinate jaise chelated forms aam taur par magnesium oxide se better absorb hote hain. Marine magnesium (Aquamin®) bhi absorption ke liye well-studied hai.",
      },
      {
        q: "Magnesium kab lena chahiye?",
        a: "Bisglycinate aksar shaam ko liya jaata hai kyunki glycine relaxation ko support kar sakta hai. Blends khane ke saath ya bina, dono tarah le sakte hain.",
      },
      {
        q: "Kya ek hi magnesium form kaafi hai?",
        a: "Agar wo aapke goal se match karti hai to ek form bhi kaam kar sakti hai. Multi-form blends ka maksad kisi ek salt ke trade-offs kam karna hai.",
      },
    ],
    productTaglines: { "mag-absorb-pro": "5-form magnesium · neend, calm, muscle recovery" },
  },

  "sleep-recovery": {
    eyebrow: "Sleep & recovery",
    h1: "Neend aur recovery support — magnesium ke calming side par bana",
    intro:
      "Behtar neend kisi ek hero ingredient se kam hi aati hai. Magnesium status, mineral balance aur shaam ka routine — teeno milke kaam karte hain, aur chhote lekin consistent inputs ek loud input se better hain.",
    overview:
      "Kharab neend aksar sub-optimal magnesium status aur raat ko unwound nervous-system tone se juri hoti hai. Magnesium bisglycinate magnesium ko glycine ke saath jodta hai — ek amino acid jise sleep onset ke role ke liye study kiya gaya hai. TruWe ka evening protocol isi combination ke around bana hai, melatonin ya sedating herbs par nahi.",
    guideHeading: "Aisa evening routine kaise banayein jo tik jaaye",
    guideIntro:
      "Consistency compound hoti hai. Strong interventions se pehle hum in levers ko priority dete hain.",
    guideItems: [
      {
        title: "Magnesium ko shaam mein fix karein",
        body: "Bisglycinate jaisi chelated form sone se 30-60 minute pehle lene par wind-down window ko support karti hai, aur din mein neend nahi laati.",
      },
      {
        title: "Aakhri ek ghanta bachaayein",
        body: "Overhead lights dim karein, kamra thanda rakhein aur sone se kam se kam aath ghante pehle caffeine band kar dein.",
      },
      {
        title: "Recovery 24-ghante ka signal hai",
        body: "Din bhar ki hydration, mineral status aur subah ki dhoop chupchaap decide karte hain ki shaam kaisi jaayegi.",
      },
      {
        title: "Kab doctor ke paas jaana hai",
        body: "Agar acchi sleep hygiene ke baad bhi kuch hafton se neend ki dikkat bani hui hai, to supplements par supplements chadhane ke bajaay clinician se baat karein.",
      },
    ],
    safety: [
      "Sleep supplements ko alcohol ya sedative dawaon ke saath medical advice ke bina na milaayein.",
      "Agar aap antidepressants, blood pressure ya neend ki dawa lete hain, to shuru karne se pehle doctor se poochein.",
      "Bachchon ke liye nahi — age ke hisaab se options ke liye TruWe Kids range dekhein.",
    ],
    faqs: [
      {
        q: "Kya magnesium din mein neend laayega?",
        a: "Bisglycinate jaisi well-tolerated forms sedative nahi hoti. Log ise shaam ko isliye lete hain kyunki tab iska asar sabse kaam ka hota hai.",
      },
      {
        q: "Kya ye melatonin jaisa hai?",
        a: "Nahi. Mineral status aur melatonin alag kaam karte hain. TruWe ke protocol mein melatonin nahi hai.",
      },
      {
        q: "Farq mehsoos hone mein kitna time lagta hai?",
        a: "Kisi bhi roz wale supplement ko judge karne se pehle 2-4 hafte consistent use dein.",
      },
    ],
    productTaglines: { "mag-absorb-pro": "5-form magnesium · neend, calm, muscle recovery" },
  },

  "inositol-supplements": {
    eyebrow: "Inositol collection",
    h1: "Inositol supplements — usi ratio par jis par research asli mein bani hai",
    intro:
      "Inositol ek nutrient hai jo aapki body pehle se banati aur use karti hai. Iske do active forms — myo-inositol aur D-chiro-inositol — ke beech ka ratio wahi hissa hai jo zyadatar consumer supplements galat karte hain.",
    overview:
      "Cycle regularity aur insulin support ke sabse zyada study kiye gaye inositol protocols myo-inositol se D-chiro-inositol ka 40:1 ratio use karte hain, jo plasma ke ratio ke kareeb hai. Saste products ya to D-chiro form hata dete hain ya aisa ratio use karte hain jo study hi nahi hua. TruWe ka Ova Balance isi 40:1 ratio par bana hai, Quatrefolic® L-5-MTHF aur chromium ke saath.",
    guideHeading: "Inositol products ko kaise compare karein",
    guideIntro:
      "Do labels par same milligrams likhe ho sakte hain aur phir bhi wo bahut alag ho sakte hain. Ye details maayne rakhti hain.",
    guideItems: [
      { title: "40:1 ratio", body: "Per daily serving 2000 mg myo-inositol aur 50 mg D-chiro-inositol dekhein." },
      {
        title: "Active folate, folic acid nahi",
        body: "Quatrefolic® L-5-MTHF folate ka metabolically active form hai — wahi jo kaafi women seedha use kar sakti hain.",
      },
      {
        title: "Sachet vs capsule",
        body: "Sachets bada daily dose carry karte hain aur paani mein ghul jaate hain — isliye clinical protocols aksar sachet use karte hain.",
      },
      {
        title: "Kya skip karein",
        body: "Wo products jo sirf D-chiro-inositol likhte hain, ya ratio ko proprietary blend ke peeche chhupa dete hain.",
      },
    ],
    safety: [
      "Agar aap pregnant hain, pregnancy plan kar rahi hain ya breastfeeding kara rahi hain, to inositol par apne obstetrician se baat karein.",
      "Agar aap metformin, insulin ya thyroid ki dawa lete hain, to inositol shuru karne se pehle clinician ko involve karein.",
      "Koi unusual side effect dikhe to band karein aur doctor se milein.",
    ],
    faqs: [
      {
        q: "40:1 ratio kyun?",
        a: "Ye plasma mein myo- aur D-chiro-inositol ke natural ratio ke lagbhag barabar hai, aur zyadatar well-designed PCOS trials isi ratio par hui hain.",
      },
      {
        q: "Farq dikhne mein kitna time lag sakta hai?",
        a: "Published protocols aam taur par 3-6 mahine consistent use ke baad outcomes dekhte hain.",
      },
      {
        q: "Kya inositol metformin ke saath le sakte hain?",
        a: "Prescription dawa khud se na badlein. Apne clinician se poochein — kuch patients ko dono saath lene ki salah di jaati hai.",
      },
    ],
    productTaglines: { "ova-balance": "40:1 Myo:D-Chiro Inositol · PCOS aur cycle support" },
  },

  "pcos-nutrition": {
    eyebrow: "PCOS nutrition",
    h1: "PCOS nutrition — wo ingredients jinhein support ke liye sabse consistently study kiya gaya hai",
    intro:
      "PCOS jitna hormonal hai, utna hi metabolic bhi. Isliye sabse strong evidence un nutrients ke saath hai jo insulin signalling, ovulatory rhythm aur mineral status ko support karte hain — kisi one-shot hormone product ke saath nahi.",
    overview:
      "Nutrition PCOS ke medical care ki jagah nahi le sakti, lekin kuch nutrients research mein baar-baar aate hain: myo-inositol aur D-chiro-inositol 40:1 par, active folate, chromium, magnesium aur zinc. TruWe inhein berberine HCl ke saath — ek well-studied AMPK activator — ek hi once-daily sachet mein deta hai.",
    guideHeading: "PCOS plan mein nutrition kahan fit hoti hai",
    guideIntro:
      "Nutrition ko ek lever samjhein — neend, movement, stress aur, jahan zaroori ho, medical treatment ke saath-saath.",
    guideItems: [
      { title: "Shuruaat inositol 40:1 se karein", body: "Pichle dus saal mein PCOS ke liye sabse zyada research ki gayi nutritional intervention." },
      { title: "Mineral gaps cover karein", body: "Magnesium, zinc aur chromium insulin signalling aur stress resilience ko support karte hain." },
      { title: "Active folate use karein", body: "L-5-MTHF (Quatrefolic®) wo active form hai jise body seedha use karti hai." },
      {
        title: "Track karein, peecha na karein",
        body: "Kisi protocol ko 3 mahine consistent use dein aur cycle length, energy aur skin dekhein — ek baar mein ek variable.",
      },
    ],
    safety: [
      "PCOS ka diagnosis aur monitoring qualified clinician se honi chahiye — supplements supportive hain, diagnosis nahi.",
      "Agar aap pregnant hain, pregnancy plan kar rahi hain ya breastfeeding kara rahi hain, to supplements add karne se pehle obstetrician se poochein.",
      "Kisi supplement plan ki wajah se prescribed dawa (metformin, oral contraceptives, thyroid, etc.) band ya badle nahi.",
    ],
    faqs: [
      {
        q: "Kya sirf supplements PCOS reverse kar sakte hain?",
        a: "Koi supplement PCOS reverse nahi kar sakta. Nutrition ek supportive lever hai — neend, movement aur zaroorat par medical care ke saath.",
      },
      {
        q: "Kya phir bhi medical follow-up chahiye?",
        a: "Haan. PCOS ek clinical diagnosis hai; regular monitoring important hai, khaaskar agar fertility goal ho.",
      },
      {
        q: "Kya ye long-term safe hai?",
        a: "Protocol ke nutrients trials mein long-term use hue hain, lekin koi bhi long-term plan apne clinician ke saath review karein.",
      },
    ],
    productTaglines: { "ova-balance": "40:1 Myo:D-Chiro Inositol · PCOS aur cycle support" },
  },

  "liver-gut-support": {
    eyebrow: "Liver & gut",
    h1: "Liver aur gut support — gut-liver axis ke around banaya gaya",
    intro:
      "Aapka liver apni blood supply ka lagbhag sattar percent gut se leta hai. Isliye liver support aur gut support ek hi baat-cheet mein — aur aksar ek hi protocol mein — aate hain.",
    overview:
      "Liver Biome Plus NAC, liposomal silymarin, liposomal curcumin aur alpha-lipoic acid ko 10 billion CFU multi-strain probiotic ke saath jodta hai. Liposomal carriers isliye hain kyunki curcumin aur silymarin apne standard forms mein absorb karna mushkil hai. Formula ka probiotic side ye maanta hai ki roz ki gut health liver ke daily kaam se upstream hai.",
    guideHeading: "Liver + gut formula mein kya dekhna chahiye",
    guideIntro:
      "Detox teas aur ek-hafte ke cleanses wo nahi karte jo unki marketing kehti hai. Roz use hone wale formulas dekhein, well-studied actives ke saath.",
    guideItems: [
      { title: "Delivery form maayne rakhti hai", body: "Standard curcumin bahut kam absorb hota hai. Liposomal delivery iske evidence-backed jawaabon mein se ek hai." },
      { title: "NAC ka decades ka use hai", body: "NAC glutathione ka well-studied precursor hai — body ke primary antioxidants mein se ek." },
      { title: "Axis ke dono side support karein", body: "Multi-strain probiotic gut wale side ko address karta hai, jise pure liver product chhod deta hai." },
      { title: "'Flush' naam ki cheezon ko ignore karein", body: "Liver flushes evidence se support nahi hote aur ulta nuksaan kar sakte hain." },
    ],
    safety: [
      "Agar aap blood thinners, liver ki prescription dawa ya chemotherapy le rahe hain, to shuru karne se pehle doctor se poochein.",
      "Pregnancy ya breastfeeding mein medical advice ke bina use na karein.",
      "Allergy ke signs ya unusual symptoms dikhein to band karein aur clinician se milein.",
    ],
    faqs: [
      {
        q: "Kya mujhe liver cleanse chahiye?",
        a: "Nahi. Aapka liver koi filter nahi hai jise beech-beech mein flush karna pade. Well-studied nutrients ka roz ka consistent support hi samajhdaari hai.",
      },
      {
        q: "Liposomal delivery kyun?",
        a: "Standard curcumin aur silymarin ki oral absorption limited hoti hai. Liposomal carriers iske better-studied jawaabon mein se ek hain.",
      },
      {
        q: "Kya ise dawa ke saath le sakte hain?",
        a: "Agar aap liver, blood clotting ya cancer therapy ki prescription dawa lete hain, to shuru karne se pehle doctor se poochein.",
      },
    ],
    productTaglines: {
      "liver-biome-plus": "NAC · liposomal silymarin · curcumin · ALA + probiotic",
    },
  },

  "kids-nutrition": {
    eyebrow: "Kids nutrition",
    h1: "Bachchon ki nutrition — added sugar ke bina",
    intro:
      "Bachchon ko nutrients chahiye, sugar nahi. TruWe Kids ek alag range hai jo badhte bachchon ke liye banayi gayi hai — no added sugar, no artificial colours, no adult-strength actives.",
    overview:
      "India mein bikne wali zyadatar kids gummies nutrition se zyada confectionery hoti hain. TruWe Kids ek dedicated range hai — age ke hisaab se doses, no added sugar, no artificial colours, aur aise flavours nahi jo toffee jaise lagein. TruWe range ke adult formulations bachchon ke liye suitable nahi hain.",
    guideHeading: "Bachchon ke supplements ke baare mein kaise sochein",
    guideIntro:
      "Pehle khana, hamesha. Supplements ek supportive layer hain — khane ya medical care ka replacement kabhi nahi.",
    guideItems: [
      { title: "Sirf age ke hisaab se doses", body: "Adult supplements paediatric nahi hote — dose, form aur delivery alag hoti hai." },
      { title: "Sugar label padhein", body: "'Healthy' gummies mein 3-4 g added sugar per serving hafte bhar mein bahut jama ho jaata hai." },
      { title: "Artificial colour se bachein", body: "Chamakdar colours marketing choice hain, nutrition ki nahi." },
      { title: "Paediatrician se poochein", body: "Khaaskar agar bachche ko allergy ho, dawa chal rahi ho ya koi medical condition ho." },
    ],
    safety: [
      "Bachche ke liye naya supplement shuru karne se pehle hamesha paediatrician se baat karein.",
      "Supplements bachchon ki pahunch se door rakhein — sugar-free gummies bhi zyada khaayi ja sakti hain.",
      "Adult supplements bachchon ko na dein; dose aur formulation compare karne layak nahi hain.",
    ],
    faqs: [
      {
        q: "Kya TruWe ke adult products bachchon ke liye safe hain?",
        a: "Nahi. Adult supplements adult physiology ke liye bane hote hain. Dedicated TruWe Kids range use karein aur paediatrician se poochein.",
      },
      { q: "Kya aap added sugar use karte hain?", a: "TruWe Kids range added sugar aur artificial colours ke bina banayi gayi hai." },
      {
        q: "Mera bachcha kis umar se shuru kar sakta hai?",
        a: "Ye product ke hisaab se badalta hai — pack par likhi age guidance padhein aur apne paediatrician se confirm karein.",
      },
    ],
  },
};

/** Routines: bundle/ritual level copy, keyed by bundle slug. */
export const BUNDLE_HI: Record<string, { title?: string; tagline: string }> = {
  "calm-sleep-duo": {
    tagline: "Gehri neend ke liye magnesium + raat bhar detox ke liye Liver Biome Plus.",
  },
  "hormone-reset-duo": {
    tagline: "Hormones ke liye Ova Balance + stress reset ke liye Mag Absorb Pro.",
  },
  "complete-wellness-trio": {
    tagline: "Neend, hormones aur gut — aapka poora daily ritual.",
  },
};

/** Quiz goal labels. */
export const GOAL_HI: Record<string, string> = {
  sleep: "Behtar neend",
  stress: "Stress aur calm",
  hormones: "Hormonal balance",
  pcos: "PCOS support",
  gut: "Gut health",
  detox: "Liver aur detox",
  immunity: "Immunity",
  recovery: "Muscle recovery",
  mood: "Mood",
};

/* ---------------- hooks ---------------- */

/** Localized long-form copy for one SKU, English fields passed in as fallback. */
export function useProductCopy() {
  const { lang } = useLanguage();
  return useCallback(
    <T extends Partial<ProductCopy>>(handle: string, english: T): T => {
      const hi = PRODUCT_HI[handle as ProductHandle];
      if (lang !== "hi-en" || !hi) return english;
      const out: Record<string, unknown> = { ...english };
      for (const key of Object.keys(english) as (keyof ProductCopy)[]) {
        const source = english[key];
        const target = hi[key];
        if (typeof source === "string") {
          out[key] = pickLocalized(lang, source, target as string);
        } else if (Array.isArray(source) && Array.isArray(target)) {
          out[key] = source.map((line, i) =>
            pickLocalized(lang, line as string, (target as string[])[i]),
          );
        } else if (source && target && typeof source === "object") {
          const merged: Record<string, string> = {};
          for (const k of Object.keys(source as object)) {
            merged[k] = pickLocalized(
              lang,
              (source as Record<string, string>)[k],
              (target as Record<string, string>)[k],
            );
          }
          out[key] = merged;
        }
      }
      return out as T;
    },
    [lang],
  );
}

/** Localized routine (how-to-use) line for a SKU. */
export function useProductRoutine() {
  const { lang } = useLanguage();
  return useCallback(
    (handle: string) => {
      const en = PRODUCT_ROUTINE_EN[handle as ProductHandle] ?? "";
      return pickLocalized(lang, en, PRODUCT_HI[handle as ProductHandle]?.routine);
    },
    [lang],
  );
}

/**
 * Localized collection page. Returns the same shape as the English page with
 * every translated field swapped in; untranslated fields (SEO strings,
 * ingredient names, article titles) stay exactly as written in English.
 */
export function useLocalizedCollection<
  T extends {
    slug: string;
    h1: string;
    eyebrow: string;
    intro: string;
    overview: string;
    selectionGuide: { heading: string; intro: string; items: { title: string; body: string }[] };
    safety: string[];
    faqs: { q: string; a: string }[];
    products: { handle: string; tagline: string }[];
  },
>(collection: T): T {
  const { lang } = useLanguage();
  const hi = COLLECTION_HI[collection.slug as CollectionSlug];
  if (lang !== "hi-en" || !hi) return collection;
  return {
    ...collection,
    h1: pickLocalized(lang, collection.h1, hi.h1),
    eyebrow: pickLocalized(lang, collection.eyebrow, hi.eyebrow),
    intro: pickLocalized(lang, collection.intro, hi.intro),
    overview: pickLocalized(lang, collection.overview, hi.overview),
    selectionGuide: {
      heading: pickLocalized(lang, collection.selectionGuide.heading, hi.guideHeading),
      intro: pickLocalized(lang, collection.selectionGuide.intro, hi.guideIntro),
      items: collection.selectionGuide.items.map((item, i) => ({
        title: pickLocalized(lang, item.title, hi.guideItems[i]?.title),
        body: pickLocalized(lang, item.body, hi.guideItems[i]?.body),
      })),
    },
    safety: collection.safety.map((s, i) => pickLocalized(lang, s, hi.safety[i])),
    faqs: collection.faqs.map((f, i) => ({
      q: pickLocalized(lang, f.q, hi.faqs[i]?.q),
      a: pickLocalized(lang, f.a, hi.faqs[i]?.a),
    })),
    products: collection.products.map((p) => ({
      ...p,
      tagline: pickLocalized(
        lang,
        p.tagline,
        hi.productTaglines?.[p.handle as ProductHandle] ??
          PRODUCT_HI[p.handle as ProductHandle]?.tagline,
      ),
    })),
  };
}

/** Localized SKU_META-style routine copy (tagline, why, 7/15/30-day timeline). */
export function useSkuRoutineCopy() {
  const { lang } = useLanguage();
  return useCallback(
    <T extends { tagline: string; why: string; timeline: { day7: string; day15: string; day30: string } }>(
      handle: string,
      meta: T,
    ): T => {
      const hi = PRODUCT_HI[handle as ProductHandle];
      if (lang !== "hi-en" || !hi) return meta;
      return {
        ...meta,
        tagline: pickLocalized(lang, meta.tagline, hi.tagline),
        why: pickLocalized(lang, meta.why, hi.why),
        timeline: {
          day7: pickLocalized(lang, meta.timeline.day7, hi.timeline.day7),
          day15: pickLocalized(lang, meta.timeline.day15, hi.timeline.day15),
          day30: pickLocalized(lang, meta.timeline.day30, hi.timeline.day30),
        },
      };
    },
    [lang],
  );
}

/** Localized bundle (routine) tagline, keyed by bundle slug. */
export function useBundleTagline() {
  const { lang } = useLanguage();
  return useCallback(
    (slug: string, english: string | null | undefined) =>
      pickLocalized(lang, english, BUNDLE_HI[slug]?.tagline),
    [lang],
  );
}

/** Localized quiz goal label. */
export function useGoalLabel() {
  const { lang } = useLanguage();
  return useCallback(
    (goal: string, english: string) => pickLocalized(lang, english, GOAL_HI[goal]),
    [lang],
  );
}

/** Localized product description + tagline for the PDP and cards. */
export function useProductDescription() {
  const { lang } = useLanguage();
  return useCallback(
    (handle: string, english: string) =>
      pickLocalized(lang, english, PRODUCT_HI[handle as ProductHandle]?.description),
    [lang],
  );
}

/** Localized short product tagline. */
export function useProductTagline() {
  const { lang } = useLanguage();
  return useCallback(
    (handle: string, english: string) =>
      pickLocalized(lang, english, PRODUCT_HI[handle as ProductHandle]?.tagline),
    [lang],
  );
}

/**
 * Sentence-level pairs for long-form copy that has no stable key of its own
 * (product blurbs on /shop, /ingredients and similar). Keyed by the exact
 * English sentence, so an English edit safely falls back to English until the
 * Hinglish half is rewritten.
 */
export const SENTENCE_HI: Record<string, string> = {
  // /shop product blurbs
  "Sleep · Stress · Recovery": "Neend · Stress · Recovery",
  "Liver · Gut · Daily detox": "Liver · Gut · Roz ka detox",
  "PCOS · Hormones · Skin": "PCOS · Hormones · Skin",

  // /ingredients product taglines
  "5-stage magnesium matrix for deep sleep, calm and muscle recovery.":
    "5-stage magnesium matrix — gehri neend, calm aur muscle recovery ke liye.",
  "Research-backed PCOS sachet for cycle regularity, ovulation and metabolic health.":
    "Research-backed PCOS sachet — cycle regularity, ovulation aur metabolic health ke liye.",
  "Triple Liposomal liver detox + gut microbiome reset in one daily protocol.":
    "Triple Liposomal liver detox + gut microbiome reset, ek hi daily protocol mein.",

  // routine framing
  "Once daily · 30-day supply": "Roz ek baar · 30-din ki supply",
};

/** Translate a known English sentence; unknown sentences stay in English. */
export function useSentence() {
  const { lang } = useLanguage();
  return useCallback(
    (english: string) => pickLocalized(lang, english, SENTENCE_HI[english]),
    [lang],
  );
}
